import { FormattedMessage, FormattedNumber } from 'react-intl';
import type CurrencyCode from '@alltrails/core/types/CurrencyCode';
import Bell from '@alltrails/denali/icons/Bell';
import LockOpen from '@alltrails/denali/icons/LockOpen';
import Star from '@alltrails/denali/icons/Star';
import classNames from 'classnames';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import styles from './TrialReminder.module.scss';

type Props = {
  currencyCode: CurrencyCode;
  cost: number;
  tier: PurchaseTier;
  selected: boolean;
  variant?: 'default' | 'minimal';
};

const TrialReminderRow = ({
  icon,
  message,
  tier,
  variant
}: {
  icon: React.ReactNode;
  message: React.ReactNode;
  tier: PurchaseTier;
  variant: 'default' | 'minimal';
}) => {
  return (
    <div className={classNames(styles.trialReminderRow, styles[variant])}>
      <span className={classNames(styles.trialReminderRowIcon, { [styles.plus]: tier === 'plus', [styles.peak]: tier === 'peak' })}>{icon}</span>
      <span>{message}</span>
    </div>
  );
};

// Designs: https://www.figma.com/design/PtYSSpohQ9rL7ZWCcdsYJK/2025-Upsell-System?node-id=14878-213417&t=QkyXqJzur1tWw9aA-4
const TrialReminder = ({ currencyCode, cost, tier, selected, variant = 'default' }: Props) => {
  const iconColor = tier === 'peak' ? '--color-text-peak' : '--color-text-plus';
  const iconSize = variant === 'default' ? 'sm' : 'xs';
  return (
    <div className={classNames(styles.trialReminderWrapper, styles[variant], { [styles.selected]: selected })}>
      <div className={styles.trialReminderContainer}>
        <TrialReminderRow
          icon={<LockOpen color={iconColor} size={iconSize} />}
          message={<FormattedMessage defaultMessage="<b>Today:</b> Start your free trial" values={{ b: chunks => <b>{chunks}</b> }} />}
          tier={tier}
          variant={variant}
        />
        <TrialReminderRow
          icon={<Bell color={iconColor} size={iconSize} />}
          message={<FormattedMessage defaultMessage="<b>Day 5:</b> Get a trial reminder" values={{ b: chunks => <b>{chunks}</b> }} />}
          tier={tier}
          variant={variant}
        />
        <TrialReminderRow
          icon={<Star color={iconColor} size={iconSize} />}
          message={
            <FormattedMessage
              defaultMessage="<b>Day 7:</b> You’ll be charged {annualCost}/year"
              values={{ b: chunks => <b>{chunks}</b>, annualCost: <FormattedNumber currency={currencyCode} style="currency" value={cost} /> }}
            />
          }
          tier={tier}
          variant={variant}
        />
      </div>
    </div>
  );
};

export default TrialReminder;
